import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, AlertCircle, Upload, FileText, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TwoFASetup } from '../components/TwoFASetup';
import RecoverPassword from './Recoverpassword';

export default function LoginPage() {
  const navigate = useNavigate();
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [showPasswordStep, setShowPasswordStep] = useState(false);
  
  // MPC Backup file states
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [backupFileContent, setBackupFileContent] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showBackupField, setShowBackupField] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  
  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [showRecoverPassword, setShowRecoverPassword] = useState(false);
  const [tempUserData, setTempUserData] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-focus 2FA input when shown
  useEffect(() => {
    if (show2FA) {
      const input = document.getElementById('2fa-input');
      input?.focus();
    }
  }, [show2FA]);

  // Handle MPC backup file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file extension
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.fixorium')) {
      toast({
        title: "INVALID FILE",
        description: "Please select a valid .fixorium backup file",
        variant: "destructive",
      });
      return;
    }
    
    setUploadingFile(true);
    setBackupFile(file);
    
    try {
      const content = await file.text();
      // Validate JSON format
      const parsed = JSON.parse(content);
      if (parsed.magic !== "FIXORIUM-MPC") {
        throw new Error("Invalid backup file format");
      }
      setBackupFileContent(content);
      
      toast({
        title: "BACKUP LOADED",
        description: "Backup file validated. Click Login again to recover your wallet.",
      });
      
    } catch (err) {
      toast({
        title: "INVALID FILE",
        description: "The selected file is not a valid Fixorium backup",
        variant: "destructive",
      });
      setBackupFile(null);
      setBackupFileContent(null);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleContinue = () => {
    if (!email.trim()) {
      setError('EMAIL IS REQUIRED');
      return;
    }
    setShowPasswordStep(true);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const requestBody: any = {
        email: email.trim(),
        password,
      };
      
      if (backupFileContent) {
        requestBody.backupFile = backupFileContent;
      }
      
      if (twoFACode) {
        requestBody.twoFACode = twoFACode;
      }
      
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.status === 401) {
        if (data.error && (
          data.error.includes("backup") || 
          data.error.includes("MPC") || 
          data.error.includes("wallet") ||
          data.code === "MPC_RECOVERY_FAILED" ||
          data.code === "MISSING_SHARE_B"
        )) {
          setShowBackupField(true);
          setError("Please upload your .fixorium backup file to login.");
        } else {
          setError(data.error || "Invalid email or password");
        }
        setLoading(false);
        return;
      }

      if (data.status === '2fa_setup_required' || data.require2FASetup === true) {
        setTempUserData({ 
          userId: data.userId, 
          email: email.trim(),
          isMPC: data.isMPC || false
        });
        setShow2FASetup(true);
        setLoading(false);
        return;
      }

      if (data.status === '2fa_required' || data.require2FA === true) {
        setShow2FA(true);
        setUserId(data.userId);
        setTempUserData({ 
          userId: data.userId,
          email: email.trim(),
          requires2FA: true,
          isMPC: data.isMPC || false
        });
        setError('');
        setLoading(false);
        
        toast({
          title: "2fa Authentication Required",
          description: "Please enter the 6-digit code ",
          variant: "default",
        });
        return;
      }

      if (response.status === 200 && data.success === true && data.status === 'ok') {
        const recoveryMsg = data.mpcRecovered
          ? "Wallet recovered from backup file."
          : "";

        toast({
          title: "Welcome Back!",
          description: recoveryMsg || `Successfully logged in as ${data.user.email}`,
          variant: "default",
        });

        setBackupFile(null);
        setBackupFileContent(null);
        setShowBackupField(false);

        setTimeout(() => {
          window.dispatchEvent(new Event("userUpdated"));
        }, 50);

        setTimeout(() => navigate('/dashboard'), 100);
        return;
      }

      setError(data.error || "Login failed. Please try again.");
      setLoading(false);

    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please check your connection and try again.');
      
      toast({
        title: "Connection Error",
        description: "Unable to connect to the server. Please try again.",
        variant: "destructive",
      });
      
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowRecoverPassword(false);
    setShow2FA(false);
    setShow2FASetup(false);
    setShowBackupField(false);
    setShowPasswordStep(false);
    setTwoFACode('');
    setError('');
    setTempUserData(null);
    setBackupFile(null);
    setBackupFileContent(null);
    setUserId(null);
    setEmail('');
    setPassword('');
  };

  const handle2FASetupComplete = async () => {
    toast({
      title: "2FA Enabled Successfully",
      description: "Your account is now secure. Please login again.",
      variant: "default",
    });
    
    setShow2FASetup(false);
    setTempUserData(null);
    setShow2FA(false);
    setShowBackupField(false);
    setShowPasswordStep(false);
    setTwoFACode('');
    setPassword('');
    setEmail('');
    setBackupFile(null);
    setBackupFileContent(null);
    setUserId(null);
  };

  const handleGoToSignup = () => {
    navigate('/signup');
  };

  const handleRecoverPassword = () => {
    setShowRecoverPassword(true);
  };

  const handleLoginWithGoogle = () => {
    toast({
      title: "COMING SOON",
      description: "Google login will be available soon.",
    });
  };

  const handleLoginWithTelegram = () => {
    toast({
      title: "COMING SOON",
      description: "Telegram login will be available soon.",
    });
  };

  if (showRecoverPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <RecoverPassword onBack={handleBackToLogin} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <form onSubmit={showPasswordStep ? handleSubmit : (e) => e.preventDefault()} className="space-y-5">
            
            {/* LOGIN Title */}
            <h2 className="text-2xl font-bold text-foreground">LOGIN</h2>

            {!show2FA ? (
              <>
                {/* Email Input */}
                <div>
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={!showPasswordStep}
                    className="w-full bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground focus:border-yellow-500 transition-all duration-300 pl-3 pr-3 py-2"
                    autoComplete="email"
                    disabled={loading || showPasswordStep}
                  />
                </div>

                {/* Continue Button */}
                {!showPasswordStep && (
                  <Button
                    type="button"
                    onClick={handleContinue}
                    disabled={loading || !email.trim()}
                    className="w-full h-12 text-base bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg"
                  >
                    CONTINUE
                  </Button>
                )}

                {/* OR Separator */}
                {!showPasswordStep && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="px-3 bg-background text-muted-foreground uppercase text-xs">OR</span>
                    </div>
                  </div>
                )}

                {/* Google Button */}
                {!showPasswordStep && (
                  <Button
                    type="button"
                    onClick={handleLoginWithGoogle}
                    variant="outline"
                    className="w-full h-12 text-base border-border text-foreground hover:bg-muted rounded-lg"
                  >
                    LOGIN WITH GOOGLE
                  </Button>
                )}

                {/* Telegram Button */}
                {!showPasswordStep && (
                  <Button
                    type="button"
                    onClick={handleLoginWithTelegram}
                    variant="outline"
                    className="w-full h-12 text-base border-border text-foreground hover:bg-muted rounded-lg"
                  >
                    LOGIN WITH TELEGRAM
                  </Button>
                )}

                {/* Password Step */}
                {showPasswordStep && (
                  <>
                    <div>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="w-full bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground focus:border-yellow-500 transition-all duration-300 pl-3 pr-10 py-2"
                          autoComplete="current-password"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Forgot Password */}
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={handleRecoverPassword}
                        className="text-xs text-yellow-500 hover:text-yellow-600 transition-colors"
                      >
                        FORGET PASSWORD?
                      </button>
                    </div>

                    {/* Backup File Upload */}
                    {showBackupField && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-yellow-500 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          MPC BACKUP FILE REQUIRED (.FIXORIUM)
                        </label>
                        <div className="flex items-center gap-3">
                          <label className="flex-1">
                            <div className={`flex items-center justify-between w-full px-4 py-3 border rounded-lg cursor-pointer transition-all duration-200 ${backupFile ? 'border-green-500 bg-green-500/10' : 'border-yellow-500 bg-yellow-500/10 hover:bg-yellow-500/20'}`}>
                              <span className="text-sm text-foreground flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                {backupFile ? backupFile.name : "SELECT .FIXORIUM BACKUP FILE"}
                              </span>
                              <Upload className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <input
                              type="file"
                              accept=".fixorium"
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={loading || uploadingFile}
                            />
                          </label>
                        </div>
                        {uploadingFile && (
                          <p className="text-xs text-yellow-500 flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            VALIDATING BACKUP FILE...
                          </p>
                        )}
                        {backupFile && !uploadingFile && (
                          <p className="text-xs text-green-500 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            BACKUP FILE LOADED. CLICK LOGIN AGAIN
                          </p>
                        )}
                        <p className="text-xs text-yellow-500">
                          YOUR .FIXORIUM FILE IS REQUIRED
                        </p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading || !password}
                      className="w-full h-12 text-base bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          {show2FA ? 'VERIFYING...' : 'PROCESSING...'}
                        </div>
                      ) : (
                        show2FA ? "VERIFY & LOGIN" : "LOGIN"
                      )}
                    </Button>

                    {/* OR Separator */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="px-3 bg-background text-muted-foreground uppercase text-xs">OR</span>
                      </div>
                    </div>

                    {/* Google Button - Password step */}
                    <Button
                      type="button"
                      onClick={handleLoginWithGoogle}
                      variant="outline"
                      className="w-full h-12 text-base border-border text-foreground hover:bg-muted rounded-lg"
                    >
                      LOGIN WITH GOOGLE
                    </Button>

                    {/* Telegram Button - Password step */}
                    <Button
                      type="button"
                      onClick={handleLoginWithTelegram}
                      variant="outline"
                      className="w-full h-12 text-base border-border text-foreground hover:bg-muted rounded-lg"
                    >
                      LOGIN WITH TELEGRAM
                    </Button>

                    {/* Back to email */}
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPasswordStep(false);
                          setPassword('');
                          setError('');
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        ← BACK TO EMAIL
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              /* 2FA Section */
              <>
                <div className="space-y-4">
                  {backupFile && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-green-300">
                          BACKUP VERIFIED. COMPLETE 2FA TO LOGIN.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">AUTHENTICATION CODE</label>
                    <Input
                      id="2fa-input"
                      type="text"
                      placeholder="000000"
                      value={twoFACode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setTwoFACode(value);
                      }}
                      required
                      maxLength={6}
                      className="w-full bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground text-center text-2xl font-mono tracking-wider focus:border-yellow-500 transition-all duration-300 rounded-none pl-3 pr-3 py-2"
                      autoFocus
                      disabled={loading}
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-500">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || twoFACode.length !== 6}
                  className="w-full h-12 text-base bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      VERIFYING...
                    </div>
                  ) : (
                    "VERIFY & LOGIN"
                  )}
                </Button>
              </>
            )}

            {/* Sign up link */}
            {!show2FA && !showPasswordStep && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  DON'T HAVE AN ACCOUNT?{' '}
                  <button
                    type="button"
                    onClick={handleGoToSignup}
                    className="text-yellow-500 hover:text-yellow-600 font-semibold transition-colors"
                  >
                    SIGN UP
                  </button>
                </p>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && tempUserData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="max-w-md w-full">
            <TwoFASetup
              userId={tempUserData.userId}
              email={tempUserData.email}
              onComplete={handle2FASetupComplete}
              onClose={() => {}}
              mandatory={true}
            />
          </div>
        </div>
      )}
    </>
  );
}
